import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "adeshwar_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Validate each item — drop anything malformed so a poisoned
      // localStorage value can't corrupt the UI.
      return parsed.filter(
        (i) =>
          i &&
          typeof i.product_id === "string" &&
          typeof i.name === "string" &&
          Number.isFinite(i.price) &&
          Number.isInteger(i.qty) &&
          i.qty > 0
      );
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,          name: product.name,
          price: product.price,
          base_price: product.price,
          discount_pct:
            product.mrp && product.mrp > product.price
              ? Math.round((1 - product.price / product.mrp) * 100)
              : 0,
          discount_edited: false,
          mrp: product.mrp,
          hsn_code: product.hsn_code || null,
          image: product.image,
          brand: product.brand,
          qty,
          variantInfo: product.variantInfo || null,
        },
      ];
    });
  };

  const setLineDiscount = (productId, pct) => {
    const p = Math.min(99, Math.max(0, Number(pct) || 0));
    setItems((prev) =>
      prev.map((i) => {
        if (i.product_id !== productId) return i;
        // Discount is always applied on MRP (e.g. MRP 100, 55% → 45 + GST on 45).
        const base = i.mrp || i.base_price || i.price;
        return { ...i, discount_pct: p, discount_edited: true, price: Math.round(base * (1 - p / 100) * 100) / 100 };
      })
    );
  };

  const updateQty = (productId, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId ? { ...i, qty } : i))
        .filter((i) => i.qty > 0)
    );
  };

  const removeItem = (productId) =>
    setItems((prev) => prev.filter((i) => i.product_id !== productId));

  const clearCart = () => setItems([]);

  const { subtotal, mrpTotal, totalQty } = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    const mrpTotal = items.reduce((s, i) => s + (i.mrp || i.price) * i.qty, 0);
    const totalQty = items.reduce((s, i) => s + i.qty, 0);
    return { subtotal, mrpTotal, totalQty };
  }, [items]);

  const value = {
    items, addItem, updateQty, removeItem, clearCart, setLineDiscount,
    open, setOpen,
    subtotal, mrpTotal, totalQty,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
